import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import ProgressBar from '../../components/ProgressBar';

// Mirror Party A questions for Party B to respond to
const QUESTIONS_BY_TYPE = {
  late_payment: [
    {
      id: 'claim_validity',
      question: 'Do you agree that there was a breach of contract?',
      type: 'select',
      options: ['Yes, completely', 'Partially - with explanations', 'No, I disagree'],
      required: true,
    },
    {
      id: 'response_explanation',
      question: 'Please explain your position on this claim',
      type: 'textarea',
      placeholder: 'Describe your understanding of what happened and why',
      required: true,
    },
    {
      id: 'amount_disputed',
      question: 'What amount do you believe is actually owed (if any)?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'counter_evidence',
      question: 'What evidence do you have to support your position?',
      type: 'textarea',
      placeholder: 'Describe your documents, communications, or other evidence',
      required: true,
    },
    {
      id: 'mitigating_factors',
      question: 'Are there any mitigating circumstances?',
      type: 'textarea',
      placeholder: 'Reasons for delay, extenuating circumstances, etc.',
      required: false,
    },
  ],
  security_deposit: [
    {
      id: 'damage_agreement',
      question: 'Do you agree that damage occurred as described?',
      type: 'select',
      options: ['Yes, completely', 'Partially - with explanations', 'No, I disagree'],
      required: true,
    },
    {
      id: 'response_explanation',
      question: 'Please explain your position',
      type: 'textarea',
      placeholder: 'Your understanding of the damage and responsibility',
      required: true,
    },
    {
      id: 'amount_disputed',
      question: 'What repair cost do you believe is fair (if any)?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'counter_evidence',
      question: 'What evidence do you have?',
      type: 'textarea',
      placeholder: 'Photos, inspection reports, move-in condition documentation',
      required: true,
    },
  ],
  personal_loan: [
    {
      id: 'debt_agreement',
      question: 'Do you agree that you owe this amount?',
      type: 'select',
      options: ['Yes, completely', 'Partially - with explanations', 'No, I disagree'],
      required: true,
    },
    {
      id: 'response_explanation',
      question: 'Please explain your position',
      type: 'textarea',
      placeholder: 'Your understanding of the debt and payment terms',
      required: true,
    },
    {
      id: 'amount_acknowledged',
      question: 'What amount do you acknowledge owing (if any)?',
      type: 'number',
      placeholder: '€',
      required: true,
    },
    {
      id: 'payment_ability',
      question: 'What is your current ability to pay?',
      type: 'textarea',
      placeholder: 'Financial situation, payment capacity, constraints',
      required: true,
    },
  ],
  product_dispute: [
    {
      id: 'issue_agreement',
      question: 'Do you agree there is an issue with the product/service?',
      type: 'select',
      options: ['Yes, completely', 'Partially - with explanations', 'No, I disagree'],
      required: true,
    },
    {
      id: 'response_explanation',
      question: 'Please explain your position',
      type: 'textarea',
      placeholder: 'Your understanding of the situation',
      required: true,
    },
    {
      id: 'resolution_offered',
      question: 'What resolution are you willing to provide?',
      type: 'textarea',
      placeholder: 'Refund, replacement, repair, or other solution',
      required: true,
    },
    {
      id: 'counter_evidence',
      question: 'What evidence supports your position?',
      type: 'textarea',
      placeholder: 'Product specifications, warranties, quality standards',
      required: true,
    },
  ],
  neighbor_dispute: [
    {
      id: 'issue_agreement',
      question: 'Do you agree there is a dispute as described?',
      type: 'select',
      options: ['Yes, completely', 'Partially - with explanations', 'No, I disagree'],
      required: true,
    },
    {
      id: 'response_explanation',
      question: 'Please explain your perspective',
      type: 'textarea',
      placeholder: 'Your understanding of the situation',
      required: true,
    },
    {
      id: 'responsibility_position',
      question: 'What responsibility do you accept (if any)?',
      type: 'textarea',
      placeholder: 'Your position on fault and responsibility',
      required: true,
    },
    {
      id: 'resolution_proposal',
      question: 'What resolution do you propose?',
      type: 'textarea',
      placeholder: 'How you think this should be resolved',
      required: true,
    },
  ],
};

const RespondAssessmentPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [dispute, setDispute] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDispute = async () => {
      try {
        const data = await api.getRespondView(token);
        setDispute(data);
      } catch (err) {
        setError(err.message || 'Failed to load dispute information');
      } finally {
        setLoading(false);
      }
    };

    fetchDispute();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !dispute) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full">
          <div className="text-center">
            <div className="text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold mb-2">Error</h2>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const disputeType = dispute?.disputeType || 'late_payment';
  const questions = QUESTIONS_BY_TYPE[disputeType] || QUESTIONS_BY_TYPE.late_payment;
  const currentQuestion = questions[currentQuestionIndex];

  if (!currentQuestion) {
    navigate(`/respond/${token}/parameters`);
    return null;
  }

  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  const handleNext = async () => {
    // Validate required fields
    if (currentQuestion.required && !currentAnswer.trim()) {
      setError('This question is required');
      return;
    }

    // Save current answer
    const newAnswers = {
      ...answers,
      [currentQuestion.id]: currentAnswer,
    };
    setAnswers(newAnswers);

    if (isLastQuestion) {
      // Submit all answers
      setSubmitting(true);
      setError(null);

      try {
        await api.saveRespondAssessment(token, dispute.partyBId, newAnswers);
        navigate(`/respond/${token}/parameters`);
      } catch (err) {
        setError(err.message || 'Failed to save assessment. Please try again.');
      } finally {
        setSubmitting(false);
      }
    } else {
      // Move to next question
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setCurrentAnswer(newAnswers[questions[currentQuestionIndex + 1].id] || '');
      setError(null);
    }
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setCurrentAnswer(answers[questions[currentQuestionIndex - 1].id] || '');
      setError(null);
    } else {
      navigate(`/respond/${token}/prepare`);
    }
  };

  const renderInput = () => {
    switch (currentQuestion.type) {
      case 'textarea':
        return (
          <textarea
            className="input min-h-[120px]"
            placeholder={currentQuestion.placeholder}
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        );

      case 'number':
        return (
          <div className="relative">
            <input
              type="number"
              className="input"
              placeholder={currentQuestion.placeholder}
              value={currentAnswer}
              onChange={(e) => setCurrentAnswer(e.target.value)}
              disabled={submitting}
              autoFocus
            />
          </div>
        );

      case 'select':
        return (
          <select
            className="input"
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            disabled={submitting}
            autoFocus
          >
            <option value="">Select an option</option>
            {currentQuestion.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      default:
        return (
          <input
            type="text"
            className="input"
            placeholder={currentQuestion.placeholder}
            value={currentAnswer}
            onChange={(e) => setCurrentAnswer(e.target.value)}
            disabled={submitting}
            autoFocus
          />
        );
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <ProgressBar current={2} total={4} phase="Your Assessment" />

      <div className="mb-8 text-center">
        <div className="text-sm text-gray-500 mb-2">
          Question {currentQuestionIndex + 1} of {questions.length}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
          <div
            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
          />
        </div>
      </div>

      <div className="card">
        <h2 className="text-2xl font-bold mb-6">{currentQuestion.question}</h2>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <div className="mb-6">
          {renderInput()}
          {!currentQuestion.required && (
            <p className="text-xs text-gray-500 mt-2">This question is optional</p>
          )}
        </div>

        <div className="flex justify-between pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleBack}
            className="btn-secondary"
            disabled={submitting}
          >
            Back
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : isLastQuestion ? 'Continue to Parameters' : 'Next Question'}
          </button>
        </div>
      </div>

      {/* Help text */}
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-600">
          Your answers help our AI understand your perspective and find fair settlement options.
          Be honest and thorough - your responses are private until a settlement is reached.
        </p>
      </div>
    </div>
  );
};

export default RespondAssessmentPage;
